import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Select,
  VStack,
  HStack,
  Text,
  CloseButton,
  Grid,
  GridItem,
  Divider,
} from "@chakra-ui/react";
import {
  EmployerJobAdStatus,
  EmploymentType,
  JobAdsFilters as FilterType,
} from "../types";
import { DateRangePicker } from "./DateRangePicker";

interface JobAdsFiltersProps {
  filters: FilterType;
  onFiltersChange: (filters: FilterType) => void;
  onClose: () => void;
}

export const JobAdsFilters: React.FC<JobAdsFiltersProps> = ({
  filters,
  onFiltersChange,
  onClose,
}) => {
  const [localFilters, setLocalFilters] = useState<FilterType>(filters);

  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  const handleFilterChange = (key: keyof FilterType, value: any) => {
    setLocalFilters({
      ...localFilters,
      [key]: value,
    });
  };

  const handleApplyFilters = () => {
    onFiltersChange(localFilters);
    onClose();
  };

  const handleClearFilters = () => {
    setLocalFilters({});
  };

  const hasActiveFilters = Object.values(localFilters).some(
    (value) =>
      value !== undefined &&
      value !== "" &&
      (Array.isArray(value) ? value.length > 0 : true)
  );

  return (
    <Box
      bg='white'
      p={6}
      rounded='lg'
      border='1px'
      borderColor='gray.200'
      shadow='sm'
    >
      {/* Header */}
      <Flex justify='space-between' align='center' mb={6}>
        <Text fontWeight='600' fontSize='lg' color='gray.800'>
          Filter Job Ads
        </Text>
        <CloseButton
          onClick={onClose}
          size='sm'
          color='gray.500'
          _hover={{ color: "gray.700", bg: "gray.100" }}
        />
      </Flex>

      <VStack spacing={6} align='stretch'>
        {/* Filter Controls */}
        <Grid templateColumns='repeat(auto-fit, minmax(250px, 1fr))' gap={6}>
          {/* Employment Type */}
          <GridItem>
            <FormControl>
              <FormLabel fontSize='sm' fontWeight='500' color='gray.700' mb={2}>
                Employment Type
              </FormLabel>
              <Select
                placeholder='All Employment Types'
                value={localFilters.employmentTypes?.[0] || ""}
                onChange={(e) =>
                  handleFilterChange(
                    "employmentTypes",
                    e.target.value
                      ? [e.target.value as EmploymentType]
                      : undefined
                  )
                }
                bg='gray.50'
                border='1px'
                borderColor='gray.300'
                _hover={{ borderColor: "gray.400" }}
                _focus={{
                  borderColor: "blue.500",
                  bg: "white",
                  boxShadow: "0 0 0 1px #3182ce",
                }}
                size='md'
                fontSize='sm'
              >
                <option value={EmploymentType.FULL_TIME}>Full Time</option>
                <option value={EmploymentType.CONTRACT}>Contract</option>
              </Select>
            </FormControl>
          </GridItem>

          {/* Date Range */}
          <GridItem colSpan={{ base: 1, md: 2 }}>
            <DateRangePicker
              label='Date Range'
              fromValue={localFilters.dateFrom}
              toValue={localFilters.dateTo}
              onFromChange={(value) => handleFilterChange("dateFrom", value)}
              onToChange={(value) => handleFilterChange("dateTo", value)}
              placeholder='Select date range'
            />
          </GridItem>
        </Grid>

        <Divider />

        {/* Action Buttons */}
        <Flex justify='space-between' align='center'>
          <Box>
            {hasActiveFilters && (
              <Text fontSize='sm' color='gray.600'>
                {
                  Object.values(localFilters).filter(
                    (v) =>
                      v !== undefined &&
                      v !== "" &&
                      (Array.isArray(v) ? v.length > 0 : true)
                  ).length
                }{" "}
                filter(s) active
              </Text>
            )}
          </Box>
          <HStack spacing={3}>
            {hasActiveFilters && (
              <Button
                variant='ghost'
                size='md'
                onClick={handleClearFilters}
                color='gray.600'
                _hover={{ bg: "gray.100" }}
              >
                Clear All
              </Button>
            )}
            <Button
              colorScheme='blue'
              size='md'
              onClick={handleApplyFilters}
              px={6}
            >
              Apply Filters
            </Button>
          </HStack>
        </Flex>
      </VStack>
    </Box>
  );
};
